let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;

// Engine drone nodes (persistent, cross-fade on start/stop)
let droneOsc: OscillatorNode | null = null;
let droneGain: GainNode | null = null;

// Wind ambience node (runs alongside engine drone)
let windGain: GainNode | null = null;

function getCtx(): AudioContext {
  if (!ctx) {
    ctx = new AudioContext();
    masterGain = ctx.createGain();
    masterGain.connect(ctx.destination);
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

function dest(): AudioNode {
  getCtx();
  return masterGain!;
}

function noise(duration: number, gain: number, freq = 0): void {
  const c = getCtx();
  const bufSize = Math.floor(c.sampleRate * duration);
  const buf = c.createBuffer(1, bufSize, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < bufSize; i++) data[i] = (Math.random() * 2 - 1) * 0.6;

  const src = c.createBufferSource();
  src.buffer = buf;
  const g = c.createGain();
  g.gain.setValueAtTime(gain, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + duration);

  if (freq > 0) {
    const filter = c.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = freq;
    filter.Q.value = 1;
    src.connect(filter);
    filter.connect(g);
  } else {
    src.connect(g);
  }
  g.connect(dest());
  src.start();
}

function tone(freq: number, duration: number, gain: number): void {
  const c = getCtx();
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.frequency.value = freq;
  osc.type = "sawtooth";
  g.gain.setValueAtTime(gain, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + duration);
  osc.connect(g);
  g.connect(dest());
  osc.start();
  osc.stop(c.currentTime + duration);
}

export const Audio = {
  // Wake the AudioContext and run one silent voice through the graph so
  // the first real shot doesn't pay for context/graph construction.
  warmup(): void {
    try {
      const c = getCtx();
      const g = c.createGain();
      g.gain.value = 0;
      g.connect(c.destination);
      const osc = c.createOscillator();
      osc.connect(g);
      osc.start();
      osc.stop(c.currentTime + 0.01);
    } catch {
      // Autoplay policy may refuse before a gesture — the unlock path covers it
    }
  },

  setVolume(v: number): void {
    if (masterGain) masterGain.gain.value = Math.max(0, Math.min(1, v));
  },

  startWindAmbience(): void {
    if (windGain) return;
    const c = getCtx();
    // Continuous white noise through tight bandpass — sounds like rushing air
    const bufSize = c.sampleRate * 3;
    const buf = c.createBuffer(1, bufSize, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) d[i] = Math.random() * 2 - 1;

    const src = c.createBufferSource();
    src.buffer = buf;
    src.loop = true;

    const hp = c.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 600;

    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 1800;

    windGain = c.createGain();
    windGain.gain.setValueAtTime(0, c.currentTime);
    windGain.gain.linearRampToValueAtTime(0.018, c.currentTime + 3);

    src.connect(hp);
    hp.connect(lp);
    lp.connect(windGain);
    windGain.connect(dest());
    src.start();
  },

  killStinger(): void {
    // Short descending two-tone — confirms a kill without being obnoxious
    const c = getCtx();
    for (let i = 0; i < 2; i++) {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = "sine";
      osc.frequency.value = i === 0 ? 880 : 660;
      g.gain.setValueAtTime(0, c.currentTime + i * 0.08);
      g.gain.linearRampToValueAtTime(0.06, c.currentTime + i * 0.08 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + i * 0.08 + 0.12);
      osc.connect(g);
      g.connect(dest());
      osc.start(c.currentTime + i * 0.08);
      osc.stop(c.currentTime + i * 0.08 + 0.13);
    }
  },

  squadUnderFire(): void {
    // Distressed short radio burst — squad taking fire
    const c = getCtx();
    const bufSize = Math.floor(c.sampleRate * 0.22);
    const buf = c.createBuffer(1, bufSize, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource();
    src.buffer = buf;
    const bp = c.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 900;
    bp.Q.value = 4;
    const g = c.createGain();
    g.gain.setValueAtTime(0.22, c.currentTime);
    g.gain.setValueAtTime(0.22, c.currentTime + 0.15);
    g.gain.linearRampToValueAtTime(0, c.currentTime + 0.22);
    src.connect(bp);
    bp.connect(g);
    g.connect(dest());
    src.start();
  },

  startEngineDrone(): void {
    const c = getCtx();
    if (droneOsc) return;

    // Low-pass filter (was created but never connected before — fixed)
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 160; // cut harsh harmonics, leave felt rumble

    droneGain = c.createGain();
    droneGain.gain.setValueAtTime(0, c.currentTime);
    droneGain.gain.linearRampToValueAtTime(0.045, c.currentTime + 2.5);

    // Chain: oscillators → lowpass → droneGain → master
    lp.connect(droneGain);
    droneGain.connect(dest());

    droneOsc = c.createOscillator();
    droneOsc.type = "sawtooth";
    droneOsc.frequency.value = 42;
    droneOsc.connect(lp);
    droneOsc.start();

    // Second oscillator detuned — creates slow beat, goes through same lowpass
    const osc2 = c.createOscillator();
    osc2.type = "sawtooth";
    osc2.frequency.value = 43.8; // tighter beat (1.8 Hz, ~0.55s period — less jarring than 2.5 Hz)
    const g2 = c.createGain();
    g2.gain.value = 0.55;
    osc2.connect(g2);
    g2.connect(lp);
    osc2.start();

    // Subtle LFO on the fundamental for turbulence variation
    const lfo = c.createOscillator();
    lfo.frequency.value = 0.13; // very slow wobble
    const lfoGain = c.createGain();
    lfoGain.gain.value = 1.2; // ±1.2 Hz frequency drift
    lfo.connect(lfoGain);
    lfoGain.connect(droneOsc.frequency);
    lfo.start();
  },

  stopEngineDrone(): void {
    if (!droneOsc || !droneGain || !ctx) return;
    droneGain.gain.setValueAtTime(droneGain.gain.value, ctx.currentTime);
    droneGain.gain.linearRampToValueAtTime(0, ctx.currentTime + 1.5);
    const osc = droneOsc;
    droneOsc = null;
    droneGain = null;
    setTimeout(() => { try { osc.stop(); } catch { /* already stopped */ } }, 2000);
  },

  radioChatter(): void {
    // Short filtered noise burst — simulates a radio squelch/confirmation
    const c = getCtx();
    const bufSize = Math.floor(c.sampleRate * 0.18);
    const buf = c.createBuffer(1, bufSize, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) data[i] = (Math.random() * 2 - 1);

    const src = c.createBufferSource();
    src.buffer = buf;
    const bp = c.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1200;
    bp.Q.value = 3;
    const g = c.createGain();
    g.gain.setValueAtTime(0.18, c.currentTime);
    g.gain.setValueAtTime(0.18, c.currentTime + 0.12);
    g.gain.linearRampToValueAtTime(0, c.currentTime + 0.18);
    src.connect(bp);
    bp.connect(g);
    g.connect(dest());
    src.start();
  },

  chaingunShot(): void {
    tone(180 + Math.random() * 60, 0.07, 0.12);
    noise(0.04, 0.08, 600);
  },

  cannonShot(): void {
    tone(90 + Math.random() * 20, 0.18, 0.25);
    noise(0.12, 0.15, 200);
  },

  howitzerShot(): void {
    tone(55 + Math.random() * 10, 0.35, 0.35);
    noise(0.25, 0.2, 80);
  },

  explosion(large: boolean): void {
    noise(large ? 0.9 : 0.45, large ? 0.45 : 0.28, large ? 80 : 150);
  },
};
